import React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListSubheader from "@mui/material/ListSubheader";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

import { getRightworkAPI } from "../requests/utilities/requests";
import { API_get_form_public_report } from "../../../constants";
import { resolvePiping, SHOW_IF_OP_LABELS, formatAnswer } from "./formLogic";
import { fmtDate, downloadCsv } from "./formCsvHelpers";
import FormSurveyChatPanel from "./FormSurveyChatPanel";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 1320,
  maxWidth: "95vw",
  // Fixed height (not max-height): the modal must not resize as the report
  // swaps in and out, so the body scrolls inside it.
  height: "94vh",
  bgcolor: "background.paper",
  borderRadius: "16px",
  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  border: "none",
};

// Up to this many links render as one-click tabs; beyond it, one dropdown.
const LINK_TABS_MAX = 6;

const pct = (n, d) => (d ? Math.round((n / d) * 100) : 0);

// Scale buckets to render: the current min..max range PLUS any recorded value
// outside it (a scale narrowed after responses came in keeps its field id, so
// old 6–10 answers still count toward `answered`/mean and must stay visible).
const scaleBuckets = (f) => {
  const keys = new Set();
  for (let n = f.min; n <= f.max; n++) keys.add(String(n));
  Object.keys(f.distribution || {}).forEach((k) => keys.add(k));
  return [...keys].sort((a, b) => Number(a) - Number(b));
};

/**
 * Aggregate report over a form's public-link responses: per-question
 * distributions (bars), scale means, yes/no splits, and free-text answer
 * lists. Narrow by link and by any categorical answer.
 */
function FormPublicReportModal({ open, onClose, companyId, formUuid, formName, shares }) {
  const [shareUuid, setShareUuid] = React.useState("");
  // [{field_id, value: [..]}]: several values on one question match any-of;
  // different questions must all match.
  const [filters, setFilters] = React.useState([]);
  const [report, setReport] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [addAnchor, setAddAnchor] = React.useState(null);
  const [linkAnchor, setLinkAnchor] = React.useState(null);
  const [view, setView] = React.useState("report"); // "report" | "chat"

  // Filterable questions come from the report itself (the SAVED form + labels
  // seen in submissions), never the editor's possibly-unsaved fields, so a
  // renamed-but-unsaved option can't produce a misleading empty segment.
  const [filterFields, setFilterFields] = React.useState([]);

  // Stale-fetch guard: filters can change while a request is in flight; only
  // the latest request may write state.
  const requestSeq = React.useRef(0);
  const load = React.useCallback(async () => {
    if (!formUuid) return;
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    try {
      const params = { company_id: companyId, learning_form_uuid: formUuid };
      if (shareUuid) params.share_uuid = shareUuid;
      if (filters.length) params.filters = JSON.stringify(filters);
      const res = await getRightworkAPI(API_get_form_public_report, params);
      if (seq !== requestSeq.current) return;
      const payload = res?.status === 200 ? res.data?.redux?.payload?.report : null;
      if (!payload) throw new Error("no report");
      setReport({ ...payload, filtersKey: JSON.stringify(filters) });
      if (Array.isArray(payload.filter_fields)) setFilterFields(payload.filter_fields);
    } catch (e) {
      if (seq !== requestSeq.current) return;
      setError("Couldn't load the report. Please try again.");
      setReport(null);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [companyId, formUuid, shareUuid, filters]);

  React.useEffect(() => {
    if (open) load();
  }, [open, load]);

  const toggleFilterValue = (fieldId, value) => {
    setFilters((prev) => {
      const rest = prev.filter((f) => f.field_id !== fieldId);
      const cur = prev.find((f) => f.field_id === fieldId)?.value || [];
      const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
      return next.length ? [...rest, { field_id: fieldId, value: next }] : rest;
    });
  };
  const filterValues = (fieldId) => filters.find((x) => x.field_id === fieldId)?.value || [];
  const filterOptions = (f) => {
    if (f.type === "yes_no") return [{ label: "Yes", value: "true" }, { label: "No", value: "false" }];
    if (f.type === "checkbox") return [{ label: "Checked", value: "true" }, { label: "Unchecked", value: "false" }];
    return (f.options || []).filter(Boolean).map((label) => ({ label, value: label }));
  };

  // No respondent context in an aggregate: piped tokens read as "[answer]".
  const pipe = (text) => resolvePiping(text, {}, {}, "[answer]");

  const exportCsv = () => {
    if (!report) return;
    const rows = [["Question", "Answer", "Count", "Percent of answered"]];
    (report.fields || []).forEach((f) => {
      if (f.type === "heading") return;
      const label = pipe(f.label);
      if (f.options) {
        f.options.forEach((o) => rows.push([label, o.label, o.count, `${pct(o.count, f.answered)}%`]));
      } else if (f.yes !== undefined) {
        rows.push([label, "Yes", f.yes, `${pct(f.yes, f.answered)}%`]);
        rows.push([label, "No", f.no, `${pct(f.no, f.answered)}%`]);
      } else if (f.distribution) {
        scaleBuckets(f).forEach((k) => {
          const c = f.distribution[k] || 0;
          rows.push([label, k, c, `${pct(c, f.answered)}%`]);
        });
        rows.push([label, "Mean", f.mean ?? "", ""]);
        if (f.type === "nps") {
          rows.push([label, "Promoters (9-10)", f.promoters, `${pct(f.promoters, f.answered)}%`]);
          rows.push([label, "Passives (7-8)", f.passives, `${pct(f.passives, f.answered)}%`]);
          rows.push([label, "Detractors (0-6)", f.detractors, `${pct(f.detractors, f.answered)}%`]);
          rows.push([label, "NPS", f.nps_score ?? "", ""]);
        }
      } else if (f.answers) {
        f.answers.forEach((a) => rows.push([label, a.text, 1, ""]));
      }
      rows.push([label, "(answered)", f.answered, ""]);
      if (f.skipped) rows.push([label, "(skipped)", f.skipped, ""]);
      if (f.not_shown) rows.push([label, "(not shown)", f.not_shown, ""]);
      if (f.type_changed) rows.push([label, "(excluded: question type changed)", f.type_changed, ""]);
    });
    downloadCsv(rows, `${(formName || "form").replace(/[^a-z0-9]+/gi, "_")}_report.csv`);
  };

  // Link controls stay disabled until the first (all-links) report lands, so
  // a scoped request can't win the race and leave the other tabs countless.
  // Per-link counts come from the report and reflect the answer filters. A
  // report scoped to one link only returns that link, so MERGE into the cache
  // (other tabs keep their numbers, and nothing blanks while loading), but the
  // cache is keyed by the filter set: counts from two different filter sets
  // must never sit side by side.
  const filtersKey = JSON.stringify(filters);
  const shareCountsRef = React.useRef({ key: filtersKey, counts: {} });
  if (shareCountsRef.current.key !== filtersKey) {
    shareCountsRef.current = { key: filtersKey, counts: {} };
  }
  if (report && Array.isArray(report.shares) && report.filtersKey === filtersKey) {
    report.shares.forEach((sh) => {
      shareCountsRef.current.counts[sh.uuid] = sh.count;
    });
  }
  const shareCount = (uuid) => shareCountsRef.current.counts[uuid];
  const shareLabel = (uuid) => {
    const sh = (shares || []).find((s) => s.uuid === uuid);
    return sh ? sh.label || "Default link" : "";
  };
  const filterableFields = filterFields.filter((f) => f.label);
  // Plain-English description of the slice the chat is reasoning over.
  const sliceLabel = (() => {
    const n = report?.matched;
    const parts = [];
    if (shareUuid) parts.push(shareLabel(shareUuid));
    filters.forEach((fl) => {
      const f = filterableFields.find((x) => x.id === fl.field_id);
      if (!f) return;
      const labels = fl.value.map((v) => filterOptions(f).find((o) => o.value === v)?.label ?? v);
      parts.push(`${pipe(f.label)}: ${labels.join(" or ")}`);
    });
    const count = n === undefined || n === null ? "the" : `${n}`;
    const noun = n === 1 ? "response" : "responses";
    return parts.length ? `${count} ${noun} matching ${parts.join("; ")}` : `all ${count === "the" ? "" : count + " "}${noun}`.replace(/\s+/g, " ");
  })();
  const fieldsById = React.useMemo(
    () => Object.fromEntries((report?.fields || []).map((f) => [f.id, f])),
    [report]
  );

  // Filter groups, rendered in the report header or under the chat header.
  const filtersBlock = (
    <>
    {/* Filters: which link the responses came from, then which answers
        a respondent must have given. Two labeled groups instead of one
        undifferentiated row of dropdowns. */}
    <div className="flex flex-wrap items-start gap-x-8 gap-y-4">
      {(shares || []).length > 1 && (
        <div className="shrink-0">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
            Responses from
          </div>
          {shares.length <= LINK_TABS_MAX ? (
            <div className="flex items-center gap-1.5 flex-wrap">
              <LinkTab active={!shareUuid} onClick={() => setShareUuid("")} label="All links" disabled={!report} />
              {shares.map((sh) => (
                <LinkTab
                  key={sh.uuid}
                  active={shareUuid === sh.uuid}
                  onClick={() => setShareUuid(sh.uuid)}
                  label={sh.label || "Default link"}
                  count={shareCount(sh.uuid)}
                  disabled={!report}
                />
              ))}
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={(e) => setLinkAnchor(e.currentTarget)}
                disabled={!report}
                className={`h-[30px] px-2.5 rounded-lg border text-xs inline-flex items-center gap-2 max-w-[280px] disabled:opacity-60 ${
                  shareUuid
                    ? "border-violet-700 bg-violet-50 text-violet-900 font-medium"
                    : "border-gray-200 bg-white text-slate-700 hover:border-violet-300"
                }`}
              >
                <span className="truncate">{shareUuid ? shareLabel(shareUuid) : "All links"}</span>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="shrink-0 text-slate-400">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
              <Menu
                anchorEl={linkAnchor}
                open={Boolean(linkAnchor)}
                onClose={() => setLinkAnchor(null)}
                PaperProps={{ sx: { maxHeight: 360, minWidth: 220, borderRadius: "10px" } }}
              >
                <MenuItem
                  selected={!shareUuid}
                  onClick={() => {
                    setShareUuid("");
                    setLinkAnchor(null);
                  }}
                  sx={{ fontSize: 12 }}
                >
                  All links
                </MenuItem>
                {shares.map((sh) => (
                  <MenuItem
                    key={sh.uuid}
                    selected={shareUuid === sh.uuid}
                    onClick={() => {
                      setShareUuid(sh.uuid);
                      setLinkAnchor(null);
                    }}
                    sx={{ fontSize: 12, display: "flex", justifyContent: "space-between", gap: 2 }}
                  >
                    <span className="truncate">{sh.label || "Default link"}</span>
                    <span className="text-slate-400 tabular-nums">{shareCount(sh.uuid) ?? ""}</span>
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </div>
      )}
      {filterableFields.length > 0 && (
        // Shares the row with the link tabs while the chips fit; once
        // they need more than ~half the width, wraps to a full line.
        <div className="flex-1 min-w-[520px] sm:min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
            Only show people who answered
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {filters.flatMap((fl) => {
              const f = filterableFields.find((x) => x.id === fl.field_id);
              if (!f) return [];
              return fl.value.map((v) => {
                const opt = filterOptions(f).find((o) => o.value === v);
                return (
                  <span
                    key={`${fl.field_id}-${v}`}
                    className="inline-flex items-center gap-1.5 h-[26px] pl-2.5 pr-1.5 rounded-full bg-violet-100 text-violet-900 text-xs font-medium max-w-full"
                  >
                    <span className="truncate">
                      {pipe(f.label)}: {opt?.label ?? v}
                    </span>
                    <button
                      type="button"
                      onClick={() => toggleFilterValue(f.id, v)}
                      className="w-4 h-4 rounded-full bg-violet-300 hover:bg-violet-400 text-violet-900 shrink-0 flex items-center justify-center"
                      aria-label="Remove filter"
                    >
                      <svg width="8" height="8" viewBox="0 0 8 8" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                        <path d="M1.5 1.5l5 5M6.5 1.5l-5 5" />
                      </svg>
                    </button>
                  </span>
                );
              });
            })}
            <button
              type="button"
              onClick={(e) => setAddAnchor(e.currentTarget)}
              className="inline-flex items-center gap-1 h-[26px] px-2.5 rounded-full border border-dashed border-gray-300 hover:border-violet-400 text-xs text-slate-600 hover:text-violet-800"
            >
              + Add a filter
            </button>
            {(filters.length > 0 || shareUuid) && (
              <button
                type="button"
                onClick={() => {
                  setFilters([]);
                  setShareUuid("");
                }}
                className="ml-1 text-xs font-medium text-violet-700 hover:text-violet-900"
              >
                Clear all
              </button>
            )}
          </div>
          <Menu
            anchorEl={addAnchor}
            open={Boolean(addAnchor)}
            onClose={() => setAddAnchor(null)}
            PaperProps={{ sx: { maxHeight: 360, minWidth: 260, borderRadius: "10px" } }}
          >
            {filterableFields.flatMap((f) => [
              <ListSubheader
                key={`h-${f.id}`}
                sx={{ fontSize: 11, fontWeight: 600, lineHeight: "28px", color: "#64748b" }}
              >
                {pipe(f.label)}
              </ListSubheader>,
              ...filterOptions(f).map((o) => {
                const on = filterValues(f.id).includes(o.value);
                return (
                  <MenuItem
                    key={`${f.id}-${o.value}`}
                    onClick={() => toggleFilterValue(f.id, o.value)}
                    sx={{ fontSize: 12, pl: 2, gap: 1 }}
                  >
                    <span
                      className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${
                        on ? "bg-violet-700 border-violet-700" : "border-gray-300 bg-white"
                      }`}
                    >
                      {on && (
                        <svg width="9" height="9" viewBox="0 0 10 10" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1.5 5.5l2.5 2.5 4.5-5" />
                        </svg>
                      )}
                    </span>
                    {o.label}
                  </MenuItem>
                );
              }),
            ])}
          </Menu>
        </div>
      )}
    </div>
    </>
  );

  return (
    <Modal open={open} onClose={onClose} disableEnforceFocus>
      <Box sx={modalStyle}>
        {/* Header scrolls on its own if many chips push it past ~45% of the
            modal, so the body and the Clear control stay reachable. */}
        {view === "report" && (
        <div className="shrink-0 max-h-[45vh] overflow-y-auto bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-xl font-bold text-slate-900">Report</h3>
              <p className="text-sm text-slate-600 truncate">{formName} · public links</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {view === "report" && report?.chat_enabled && (
                <button
                  type="button"
                  onClick={() => setView("chat")}
                  title="Chat with survey"
                  className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-600 hover:border-violet-700 hover:text-violet-700 transition-colors"
                >
                  <AutoAwesomeIcon style={{ fontSize: 18 }} />
                  <span>Chat with survey</span>
                </button>
              )}
              <button
                type="button"
                onClick={exportCsv}
                disabled={!report || loading}
                className="px-3 py-1.5 text-sm border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg font-medium disabled:opacity-50"
              >
                Export CSV
              </button>
              <button
                className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500"
                onClick={onClose}
              >
                <CloseIcon />
              </button>
            </div>
          </div>

          <div className="mt-4">{filtersBlock}</div>
        </div>
        )}

        {/* Body: the previous report stays mounted and dims while the next one
            loads, so the modal never collapses to a one-line "Loading…". */}
        {view === "chat" ? (
          <div className="flex-1 min-h-0">
            <FormSurveyChatPanel
              companyId={companyId}
              formUuid={formUuid}
              shareUuid={shareUuid}
              filterState={filters}
              sliceLabel={sliceLabel}
              onBack={() => setView("report")}
              onClose={onClose}
              filters={filtersBlock}
            />
          </div>
        ) : (
        <div className="relative flex-1 min-h-0 overflow-y-auto">
          {loading && report && (
            <div className="sticky top-3 z-10 flex justify-center pointer-events-none">
              <div className="flex items-center gap-2 bg-white border border-gray-200 shadow-md rounded-full px-3.5 py-1.5 text-xs text-slate-600">
                <span className="w-3 h-3 rounded-full border-2 border-violet-200 border-t-violet-700 animate-spin" />
                Updating…
              </div>
            </div>
          )}
          <div
            className={`px-6 py-5 transition-opacity ${loading && report ? "opacity-40 pointer-events-none" : ""}`}
          >
          {loading && !report ? (
            <div className="text-sm text-slate-500 py-10 text-center">Loading…</div>
          ) : error ? (
            <div className="text-sm text-red-600 py-10 text-center">{error}</div>
          ) : !report ? null : (
            <>
              {/* Headline counts */}
              <div className="flex items-end gap-4 flex-wrap">
                <div>
                  <div className="text-3xl font-semibold text-slate-900 tabular-nums leading-none">
                    {report.matched}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {report.matched === 1 ? "response" : "responses"}
                    {report.matched !== report.total ? (
                      <span className="text-slate-400"> of {report.total} total</span>
                    ) : null}
                  </div>
                </div>
              </div>

              {report.truncated && (
                <div className="mt-4 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600">
                  Based on the most recent {report.scanned} of {report.total} responses.
                  Export responses for the complete data.
                </div>
              )}

              {report.matched === 0 ? (
                <div className="mt-6 text-sm text-slate-500 py-8 text-center">
                  No responses yet.
                </div>
              ) : (
                <div className="mt-6 space-y-7">
                  {(report.fields || []).map((f, i) =>
                    f.type === "heading" ? (
                      <div key={f.id} className="pt-2">
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          {pipe(f.label)}
                        </div>
                      </div>
                    ) : (
                      <QuestionBlock
                        key={f.id}
                        field={f}
                        label={pipe(f.label)}
                        condition={describeShowIf(f.show_if, fieldsById, pipe)}
                      />
                    )
                  )}
                </div>
              )}
            </>
          )}
          </div>
        </div>
        )}
      </Box>
    </Modal>
  );
}

// "Shown when <parent question> is <value>" for a conditional follow-up.
function describeShowIf(rule, fieldsById, pipe) {
  if (!rule || !rule.field) return null;
  const ref = fieldsById[rule.field];
  if (!ref) return null;
  const op = SHOW_IF_OP_LABELS[rule.op] || rule.op;
  const value = rule.op === "answered" ? "" : ` ${formatAnswer(rule.value, ref.type)}`;
  return `Shown when "${pipe(ref.label)}" ${op}${value}`;
}

function LinkTab({ active, onClick, label, count, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`h-[30px] px-2.5 rounded-lg border text-xs inline-flex items-center gap-1.5 max-w-[220px] disabled:opacity-60 ${
        active
          ? "border-violet-700 bg-violet-50 text-violet-900 font-medium"
          : "border-gray-200 bg-white text-slate-700 hover:border-violet-300"
      }`}
    >
      <span className="truncate">{label}</span>
      {count !== null && count !== undefined && (
        <span className="text-slate-400 tabular-nums">{count}</span>
      )}
    </button>
  );
}

// One question's aggregate: horizontal bars for closed answers (single hue,
// the row label carries identity), a distribution + mean for scales, and a
// text list for open answers. Footnote shows skipped / not-shown counts.
function QuestionBlock({ field: f, label, condition }) {
  const answered = f.answered || 0;
  const foot = [];
  if (f.skipped) foot.push(`${f.skipped} skipped`);
  if (f.not_shown) foot.push(`${f.not_shown} not shown`);
  if (f.type_changed) foot.push(`${f.type_changed} excluded (question type changed)`);

  let body = null;
  if (f.options) {
    body = <Bars rows={f.options.map((o) => ({ label: o.label, count: o.count }))} total={answered} />;
  } else if (f.yes !== undefined) {
    body = (
      <Bars
        rows={[
          { label: f.type === "checkbox" ? "Checked" : "Yes", count: f.yes },
          { label: f.type === "checkbox" ? "Unchecked" : "No", count: f.no },
        ]}
        total={answered}
      />
    );
  } else if (f.type === "nps" && f.distribution) {
    const rows = scaleBuckets(f).map((k) => ({
      label: k,
      count: f.distribution[k] || 0,
    }));
    const score = f.nps_score;
    body = (
      <div>
        <div className="flex items-baseline gap-3 mb-2">
          <span className="text-2xl font-semibold text-slate-900 tabular-nums">
            {score === null || score === undefined ? "–" : score > 0 ? `+${score}` : score}
          </span>
          <span className="text-xs text-slate-500">
            NPS · {answered} answered
            {f.mean === null || f.mean === undefined ? "" : ` · average ${f.mean}`}
          </span>
        </div>
        <Bars
          rows={[
            { label: "Promoters (9-10)", count: f.promoters || 0 },
            { label: "Passives (7-8)", count: f.passives || 0 },
            { label: "Detractors (0-6)", count: f.detractors || 0 },
          ]}
          total={answered}
        />
        <div className="mt-3">
          <div className="text-[11px] text-slate-500 mb-1">
            {f.min_label || f.min} → {f.max_label || f.max}
          </div>
          <Bars rows={rows} total={answered} compact />
        </div>
      </div>
    );
  } else if (f.distribution) {
    const rows = scaleBuckets(f).map((k) => ({
      label: k,
      count: f.distribution[k] || 0,
    }));
    body = (
      <div>
        <div className="flex items-baseline gap-3 mb-2">
          <span className="text-2xl font-semibold text-slate-900 tabular-nums">
            {f.mean === null || f.mean === undefined ? "–" : f.mean}
          </span>
          <span className="text-xs text-slate-500">
            average of {answered} · scale {f.min}–{f.max}
            {f.min_label || f.max_label ? ` (${f.min_label || f.min} → ${f.max_label || f.max})` : ""}
          </span>
        </div>
        <Bars rows={rows} total={answered} compact />
      </div>
    );
  } else if (f.answers) {
    body = (
      <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
        {f.answers.length === 0 ? (
          <div className="text-xs text-slate-400">No answers.</div>
        ) : (
          f.answers.map((a, i) => (
            <div key={i} className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
              <div className="text-sm text-slate-800 whitespace-pre-wrap">{a.text}</div>
              <div className="text-[11px] text-slate-400 mt-0.5">
                {fmtDate(a.created_at)}
                {a.share_label ? ` · ${a.share_label}` : ""}
              </div>
            </div>
          ))
        )}
        {f.truncated && (
          <div className="text-[11px] text-slate-400">
            Showing the most recent {f.answers.length}. Export responses for the full list.
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-baseline justify-between gap-4">
        <h4 className="text-sm font-semibold text-slate-900 leading-snug">{label}</h4>
        <span className="text-[11px] text-slate-500 shrink-0 tabular-nums">
          {answered} answered{foot.length ? ` · ${foot.join(" · ")}` : ""}
        </span>
      </div>
      {condition && <div className="text-[11px] text-slate-500 mt-0.5">{condition}</div>}
      <div className="mt-2">{body}</div>
    </div>
  );
}

// One geometry for every bar in the report: the same label column, so every
// track starts at the same x down the page, and separate count / percent
// columns so those read as columns instead of ragged text. Long option labels
// wrap onto a second line rather than truncating.
const BAR_LABEL_W = "w-40";
const BAR_COUNT_W = "w-8";
const BAR_PCT_W = "w-12";

function Bars({ rows, total, compact = false }) {
  // Length is the answer's share of respondents, so a bar means the same
  // thing in every question and agrees with the percent printed beside it.
  const denom = Math.max(1, total);
  return (
    <div className={compact ? "space-y-1" : "space-y-1.5"}>
      {rows.map((r) => {
        const p = pct(r.count, total);
        return (
          <div key={r.label} className="flex items-center gap-3" title={`${r.label}: ${r.count} (${p}%)`}>
            <div className={`${BAR_LABEL_W} shrink-0 text-xs text-slate-700 text-right leading-tight`}>
              {r.label}
            </div>
            <div className="flex-1 h-3 rounded bg-slate-100 overflow-hidden">
              <div
                className="h-full rounded bg-violet-700"
                style={{
                  width: `${Math.min(100, (r.count / denom) * 100)}%`,
                  minWidth: r.count ? 4 : 0,
                }}
              />
            </div>
            <div className="shrink-0 flex items-center gap-2">
              <div className={`${BAR_COUNT_W} text-xs text-slate-600 tabular-nums text-right`}>
                {r.count}
              </div>
              <div className={`${BAR_PCT_W} text-xs text-slate-400 tabular-nums text-right`}>
                ({p}%)
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default FormPublicReportModal;
